import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useThemeStore, useRightPanelStore, useSidebarStore } from '@/lib/stores';
import { useAuthStore } from '@/lib/auth';
import {
  Sun, Moon, FileStack, PenSquare, LayoutPanelLeft,
  Search, Bell, LogOut, User, ChevronRight, Menu
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuLabel
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import CommandPalette from './CommandPalette';
import axios from 'axios';
import { API_BASE } from '@/lib/api';

export function Topbar({ showPanelButtons = false }) {
  const { toggleTheme, theme } = useThemeStore();
  const { open: openPanel } = useRightPanelStore();
  const { openMobile } = useSidebarStore();
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [cmdOpen, setCmdOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Cmd+K shortcut
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCmdOpen(true);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Load notifications
  useEffect(() => {
    loadNotifications();
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadNotifications = async () => {
    try {
      const res = await axios.get(`${API_BASE}/notifications`);
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread || 0);
    } catch (e) { /* ignore */ }
  };

  const markRead = async (id) => {
    try {
      await axios.patch(`${API_BASE}/notifications/${id}/read`);
      setNotifications(n => n.filter(x => x.id !== id));
      setUnreadCount(c => Math.max(0, c - 1));
    } catch (e) { /* ignore */ }
  };

  // Build breadcrumb from path
  const pathParts = location.pathname.split('/').filter(Boolean);

  return (
    <>
      <header
        data-testid="topbar"
        className="h-14 border-b border-border flex items-center justify-between px-3 sm:px-4 bg-background/80 backdrop-blur-xl sticky top-0 z-20"
      >
        {/* Left: Hamburger (mobile) + Breadcrumb */}
        <div className="flex items-center gap-1.5 text-sm min-w-0 overflow-hidden">
          {/* Hamburger — mobile only */}
          <Button
            variant="ghost"
            size="icon"
            onClick={openMobile}
            className="h-9 w-9 rounded-lg hover:bg-muted/50 flex-shrink-0 lg:hidden"
            data-testid="topbar-hamburger-button"
          >
            <Menu className="w-5 h-5" />
          </Button>

          {/* Breadcrumb — truncated on mobile */}
          <div className="flex items-center gap-1 overflow-hidden">
            {pathParts.map((part, i) => (
              <span key={i} className="flex items-center gap-1 flex-shrink-0">
                {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />}
                <span className={`${i === pathParts.length - 1 ? 'text-foreground font-medium' : 'text-muted-foreground'} capitalize truncate max-w-[80px] sm:max-w-[160px]`}>
                  {part.length > 20 ? `${part.slice(0, 8)}...` : part}
                </span>
              </span>
            ))}
          </div>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
          {/* Search / Cmd+K — hidden on small mobile */}
          <Button
            variant="ghost" size="sm"
            onClick={() => setCmdOpen(true)}
            data-testid="topbar-command-open-button"
            className="h-9 rounded-lg hover:bg-muted/50 text-xs text-muted-foreground gap-2 hidden md:flex"
          >
            <Search className="w-3.5 h-3.5" />
            <span>Search</span>
            <kbd className="text-[9px] font-mono bg-muted px-1.5 py-0.5 rounded">⌘K</kbd>
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setCmdOpen(true)}
            className="h-9 w-9 rounded-lg hover:bg-muted/50 md:hidden">
            <Search className="w-4 h-4" />
          </Button>

          {/* Panel buttons (only in workspace) */}
          {showPanelButtons && (
            <>
              <Separator orientation="vertical" className="h-5 mx-0.5 hidden sm:block" />
              <Button variant="ghost" size="icon" onClick={() => openPanel('artifacts')}
                data-testid="topbar-rightpanel-artifacts-button" className="h-9 w-9 rounded-lg hover:bg-muted/50 hidden sm:flex">
                <FileStack className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => openPanel('report')}
                className="h-9 w-9 rounded-lg hover:bg-muted/50 hidden sm:flex">
                <PenSquare className="w-4 h-4" />
              </Button>
              <Button variant="ghost" size="icon" onClick={() => openPanel('canvas')}
                className="h-9 w-9 rounded-lg hover:bg-muted/50 hidden sm:flex">
                <LayoutPanelLeft className="w-4 h-4" />
              </Button>
            </>
          )}

          <Separator orientation="vertical" className="h-5 mx-0.5 sm:mx-1" />

          {/* Notifications */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-9 w-9 rounded-lg hover:bg-muted/50 relative">
                <Bell className="w-4 h-4" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-destructive text-[9px] text-white rounded-full flex items-center justify-center font-bold">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-72">
              <DropdownMenuLabel className="text-xs text-muted-foreground">
                Notifications {unreadCount > 0 && `(${unreadCount})`}
              </DropdownMenuLabel>
              {notifications.length === 0 ? (
                <div className="px-3 py-4 text-center text-xs text-muted-foreground">No new notifications</div>
              ) : (
                notifications.slice(0, 5).map(n => (
                  <DropdownMenuItem key={n.id} onClick={() => markRead(n.id)} className="flex items-start gap-2 py-2">
                    <div className="w-2 h-2 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs font-medium truncate">{n.title}</p>
                      <p className="text-[10px] text-muted-foreground truncate">{n.content}</p>
                    </div>
                  </DropdownMenuItem>
                ))
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Theme toggle */}
          <Button variant="ghost" size="icon" onClick={toggleTheme}
            data-testid="topbar-theme-toggle" className="h-9 w-9 rounded-lg hover:bg-muted/50">
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>

          {/* User menu */}
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="topbar-user-menu"
                  className="h-9 w-9 rounded-lg hover:bg-muted/50">
                  <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary">
                    {user.name?.[0]?.toUpperCase() || 'U'}
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user.name}</p>
                  <p className="text-xs text-muted-foreground">{user.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/app/settings')}>
                  <User className="w-4 h-4 mr-2" /> Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); navigate('/login'); }}>
                  <LogOut className="w-4 h-4 mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button variant="ghost" size="sm" onClick={() => navigate('/login')} className="text-sm">
              Sign in
            </Button>
          )}
        </div>
      </header>

      {/* Command Palette */}
      <CommandPalette open={cmdOpen} onClose={() => setCmdOpen(false)} />
    </>
  );
}
